/* @flow */
// typecheck: ignore file
import { Container } from 'flux/utils';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import InviteButton from 'users/components/InviteButton.jsx';
import Link from 'components/Link.jsx';
import React, { Component } from 'react';
import UnreadStore from 'stores/UnreadStore';
import cx from 'classnames';
import { isPendingAdmin } from 'shapes/Affiliation';
import AffiliationStore from 'stores/AffiliationStore';
import GroupInviteModal from 'groups/components/GroupInviteModal.jsx';
import { mountModal } from 'lib/modal';
import PromptActions from 'actions/PromptActions';
import PromptStore from 'stores/PromptStore';
import { PromptNames } from 'constants/PromptConstants';

import styles from './styles/GroupAspectTabs.css';

const tabGlyph = {
  people: 'avatar-group-stroke',
  messages: 'message-bubble-stroke',
  activities: 'calendar-stroke',
  statistics: 'bar-graph-stroke',
  verify: 'bell-stroke',
};

const activeTabGlyph = {
  people: 'avatar-group-fill',
  messages: 'message-bubble-fill',
  activities: 'calendar-fill',
  statistics: 'bar-graph-fill',
  verify: 'bell-fill',
};

export class GroupAspectTabs extends Component {
  static propTypes = {
    group: React.PropTypes.instanceOf(Group).isRequired,
  };

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static getStores() {
    return [UnreadStore];
  }

  static calculateState(state, props) {
    return {
      unreadCount: UnreadStore.getUnreadCount(props.group.getUuid()),
    };
  }

  getGlyphName(basepath: string, subpath: string) {
    return this.context.router.isActive(`${basepath}/${subpath}`) ?
      activeTabGlyph[subpath] :
      tabGlyph[subpath];
  }

  canAccessAdminConsoleForGroup() {
    return (
      CurrentUser.getUser().hasFeature('web_admin_console') && (
        this.props.group.getType() === 'district' ||
        this.props.group.isStaffClass()
      )
    );
  }

  showActivityTab() {
    return (
      this.props.group.getType() === 'owned' ||
      this.props.group.getType() === 'subscribed' ||
      this.props.group.isRoot()
    );
  }

  showStatisticsTab() {
    if (this.canAccessAdminConsoleForGroup()) {
      return false;
    }

    return (
      this.props.group.getStaffClassOrgId() &&
      this.props.group.isOwnedByUser(CurrentUser.getUserId())
    );
  }

  showVerifyTab = () => {
    if (this.canAccessAdminConsoleForGroup()) {
      return false;
    }

    const organization = this.props.group.getOrganization();

    if (organization) {
      const affiliation = AffiliationStore.getAffiliation(organization.id);
      if (affiliation && isPendingAdmin(affiliation)) {
        return true;
      }
    }

    return false;
  }

  renderInviteButton() {
    const { group } = this.props;
    return (
      <div className={styles.inviteButton}>
        <InviteButton group={group} forceType="glyph" />
      </div>
    );
  }

  renderPdfCallout() {
    const { group } = this.props;
    const showCallout = !group.isRoot() &&
      group.isOwnedByUser(CurrentUser.getUserId()) &&
      !PromptStore.hasDismissed(PromptNames.PDF_CALLOUT);

    if (!showCallout) {
      return null;
    }

    return (
      <div
        className={styles.pdfDrawer}
        onClick={this.openInviteModal}>
        <span className={styles.pdfDrawerContent} >
          Get PDF instructions, add participants, or share a link to your class from here.
        </span>
      </div>
    );
  }

  openInviteModal = (e: SyntheticEvent) => {
    const { group } = this.props;
    e.preventDefault();
    PromptActions.recordDismissal(PromptNames.PDF_CALLOUT);
    mountModal(<GroupInviteModal group={group} hideOnBlur />, this);
  };

  render() {
    const { group } = this.props;
    const { unreadCount } = this.state;
    const basepath = `/classes/${group.getCode()}`;
    const activeColor = group.getAccentColor();
    const { isActive } = this.context.router;

    return (
      <nav className={styles.this}>
        <ul className={styles.tabList}>
          {this.showVerifyTab() &&
            <li className={styles.tab}>
              <Link
                to={`${basepath}/verify`}
                activeClassName={styles.activeLink}
                className={styles.link}>
                <Glyph
                  style={{
                    color: isActive(`${basepath}/verify`) ? activeColor : null,
                  }}
                  size="large"
                  name={this.getGlyphName(basepath, 'verify')}
                  className={styles.glyph}
                />
                <span>Verify</span>
              </Link>
            </li>
          }
          <li className={styles.tab}>
            <Link
              to={`${basepath}/messages`}
              className={cx({
                [styles.link]: true,
                [styles.activeLink]: isActive(`${basepath}/messages`),
              })}>
              <Glyph
                style={{
                  color: isActive(`${basepath}/messages`) ? activeColor : null,
                }}
                size="large"
                name={this.getGlyphName(basepath, 'messages')}
                className={styles.glyph}
              />
              <span>{`Messages${unreadCount > 0 ? ` (${unreadCount})` : ''}`}</span>
            </Link>
          </li>
          <li className={styles.tab}>
            <Link
              to={`${basepath}/people`}
              className={cx({
                [styles.link]: true,
                [styles.activeLink]: isActive(`${basepath}/people`),
              })}>
              <Glyph
                style={{
                  color: isActive(`${basepath}/people`) ? activeColor : null,
                }}
                size="large"
                name={this.getGlyphName(basepath, 'people')}
                className={styles.glyph}
              />
              <span>People</span>
              {!group.isRoot() && this.renderInviteButton()}
            </Link>
            {this.renderPdfCallout()}
          </li>
          {this.showActivityTab() &&
            <li className={styles.tab}>
              <Link
                to={`${basepath}/activities`}
                className={cx({
                  [styles.link]: true,
                  [styles.activeLink]: isActive(`${basepath}/activities`),
                })}>
                <Glyph
                  style={{
                    color: isActive(`${basepath}/activities`) ? activeColor : null,
                  }}
                  size="large"
                  name={this.getGlyphName(basepath, 'activities')}
                  className={styles.glyph}
                />
                <span>Activities</span>
              </Link>
            </li>
          }
          {this.showStatisticsTab() && (
            <li className={styles.tab}>
              <Link
                to={`${basepath}/statistics`}
                activeClassName={styles.activeLink}
                className={styles.link}>
                <Glyph
                  style={{
                    color: isActive(`${basepath}/statistics`) ? activeColor : null,
                  }}
                  size="large"
                  name={this.getGlyphName(basepath, 'statistics')}
                  className={styles.glyph}
                />
                <span>Statistics</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    );
  }
}

export default Container.create(GroupAspectTabs, { withProps: true, pure: false });



// WEBPACK FOOTER //
// ./src/groups/components/GroupAspectTabs.jsx